import mutation from './mutation.graphql.js'
import getYear from 'date-fns/getYear/index.js'
import parseISO from 'date-fns/parseISO/index.js'
import { useMutation } from 'Data/Api'
import { useNotifications, notifyError } from 'Logic/Notifications.js'
import { date } from 'Data/validate.js'
import { DUE_BUCKETS_TO_LABELS, REPORT_TYPES } from './helpers.js'

let REPORT_FILTER_VALIDATIONS = {
  [REPORT_TYPES.FINANCIAL]: ({ filters, notify }) => {
    if (!date(filters.reference_date)) {
      notify(notifyError('The date must be valid.'))
      return false
    }

    let current_year = getYear(new Date())
    let date_year = filters.reference_date
      ? getYear(parseISO(filters.reference_date))
      : 0

    if (current_year - date_year > 2) {
      notify(notifyError('The date cannot be older than 2 years ago.'))
      return false
    }

    return true
  },
}

export default function useDataOnSubmit(props, data) {
  let [, notify] = useNotifications()
  let [, executeMutation] = useMutation(mutation)

  return async function onSubmit({ value, originalValue, args, change }) {
    let filters = getFilterValues(value, args)
    if (!validateFilters(value, filters)) return

    let response = await executeMutation({
      report_type: value.type,
      filters,
    })
    if (response.error) {
      return notify(
        notifyError('An error has occurred when you tried to run it')
      )
    }

    change(next => {
      next.last_execution = response.data.reports_run_stock_reports
    })
  }

  function validateFilters(report, filters) {
    let validator = REPORT_FILTER_VALIDATIONS[report.type]
    if (!validator) {
      return true
    }
    return validator({ report, filters, notify })
  }

  function getFilterValues(report, args) {
    switch (report.type) {
      case REPORT_TYPES.STATEMENTS: {
        return {
          location_ids: report.filter_values.location_ids,
          min_amount: report.filter_values.min_amount,
          reference_date: report.filter_values.reference_date,
          transactions_start_date: report.filter_values.transactions_start_date,
          transactions_end_date: report.filter_values.transactions_end_date,
          include_due_0_30_days: report.filter_values.due_buckets.includes(
            DUE_BUCKETS_TO_LABELS.DUE_0_30
          ),
          include_due_31_60_days: report.filter_values.due_buckets.includes(
            DUE_BUCKETS_TO_LABELS.DUE_31_60
          ),
          include_due_61_90_days: report.filter_values.due_buckets.includes(
            DUE_BUCKETS_TO_LABELS.DUE_61_90
          ),
          include_due_91_120_days: report.filter_values.due_buckets.includes(
            DUE_BUCKETS_TO_LABELS.DUE_91_120
          ),
          include_due_more_120_days: report.filter_values.due_buckets.includes(
            DUE_BUCKETS_TO_LABELS.DUE_MORE_120_DAYS
          ),
        }
      }

      default: {
        if (!report.filter_values) {
          return {}
        }

        let data = {}
        if (report.filter_values.reference_date) {
          data.reference_date = report.filter_values.reference_date
        }
        if (report.filter_values.start_date) {
          data.start_date = report.filter_values.start_date
        }
        if (report.filter_values.end_date) {
          data.end_date = report.filter_values.end_date
        }
        if (report.filter_values.location_ids.length) {
          data.location_ids = report.filter_values.location_ids
        }
        if (report.filter_values.adjustment_type_ids) {
          data.adjustment_type_ids = report.filter_values.adjustment_type_ids
        }
        if (report.filter_values.financial_payor_types) {
          data.financial_payor_types =
            report.filter_values.financial_payor_types
        }
        if (report.filter_values.transaction_type_ids) {
          data.transaction_type_ids = report.filter_values.transaction_type_ids
        }
        return data
      }
    }
  }
}
